import { forwardRef, useState, useCallback } from 'react';
import { twMerge } from '@/lib/twMerge';

interface DefaultButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  cooldown?: number;
}

const DefaultButton = forwardRef<HTMLButtonElement, DefaultButtonProps>(
  (
    {
      className: classNameProp = '',
      onClick,
      cooldown = 1000,
      type = 'button',
      disabled,
      'aria-label': ariaLabel = 'Button',
      ...props
    },
    ref
  ) => {
    const [isSpamProtected, setIsSpamProtected] = useState(false);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isSpamProtected || disabled) {
          return;
        }

        setIsSpamProtected(true);

        if (onClick) {
          onClick(event);
        }

        setTimeout(() => {
          setIsSpamProtected(false);
        }, cooldown);
      },
      [isSpamProtected, onClick, cooldown, disabled]
    );

    const onClickProp = onClick ? handleClick : undefined;

    return (
      <button
        ref={ref}
        onClick={onClickProp}
        type={type}
        disabled={disabled || isSpamProtected}
        aria-label={ariaLabel}
        tabIndex={0}
        className={twMerge(
          'tw-bg-transparent tw-border-none tw-shadow-none tw-outline-none tw-px-0 tw-py-0 tw-mx-0 tw-my-0 tw-appearance-none focus:tw-outline-none focus:tw-ring-0 tw-text-black max-nine:tw-text-xs-custom nine:tw-max-w-xs',
          isSpamProtected ? 'tw-opacity-70' : '',
          classNameProp
        )}
        {...props}
      />
    );
  }
);

DefaultButton.displayName = 'DefaultButton';

export default DefaultButton;
